import { getThemeByName } from "../data/Themes";
import { applyTheme } from "./applyTheme";
import { changeMode, changeTheme } from "../store/slices/themeSlice";
import { findUpdateAndCreateSettings } from "../service/common/commonService";

/**
 * Single source of truth for switching the app theme. A "theme" here bundles
 * both the brand accent colour AND the light/dark mode, so selecting one
 * (e.g. "Dark Purple") recolours the whole app and flips light/dark together.
 *
 * It:
 *  1. updates Redux (`themeName` + `appMode`) so every component reacts,
 *  2. repaints the CSS custom properties via applyTheme(),
 *  3. mirrors the choice into localStorage for instant load on refresh,
 *  4. persists it to the user's server settings (Theme + Mode) when signed in.
 *
 * MainLayout already toggles the `darkTheme` body class off `appMode`, so no
 * extra DOM work is needed here.
 */
export const applyAndPersistTheme = async (
  dispatch: any,
  themeName: string
): Promise<void> => {
  const theme = getThemeByName(themeName);

  // 1 + 2. React state + CSS variables
  dispatch(changeTheme({ themeName: theme.name, appMode: "" }));
  dispatch(changeMode({ appMode: theme.mode, themeName: theme.mode }));
  applyTheme(theme.name);

  // 3. Fast path for the next page load
  localStorage.setItem("Theme-Name", JSON.stringify({ themeName: theme.name }));
  localStorage.setItem("App-Mode", JSON.stringify({ appMode: theme.mode }));

  // 4. Server persistence (skip for logged-out / incomplete sessions)
  const userId = localStorage.getItem("userId");
  const tenantId = localStorage.getItem("tenantId");
  if (userId && tenantId) {
    const base = {
      userId: JSON.parse(userId),
      tenantId: JSON.parse(tenantId),
    };
    await findUpdateAndCreateSettings(dispatch, {
      ...base,
      name: "Theme",
      value: theme.name,
    });
    await findUpdateAndCreateSettings(dispatch, {
      ...base,
      name: "Mode",
      value: theme.mode,
    });
  }
};
